/**
 * Processes Query Hooks
 *
 * Mirrors useGoalsQuery. Gives ProcessesScreen cached-instant rendering
 * on focus so tab switches don't flash a loading spinner.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { processesApi } from '../../services/apiService';
import { queryKeys, STALE_TIMES } from '../../providers/QueryProvider';

interface ProcessesListParams {
  page?: number;
  pageSize?: number;
  department_id?: number;
  strategic_goals?: number;
  search?: string;
  year?: number;
}

interface ProcessesListResponse {
  results: any[];
  count: number;
}

export function useProcessesQuery(params?: ProcessesListParams) {
  return useQuery({
    queryKey: queryKeys.processes.list(params),
    queryFn: async (): Promise<ProcessesListResponse> => {
      const queryParams: Record<string, any> = {};
      if (params?.page) queryParams.page = params.page;
      if (params?.pageSize) queryParams.page_size = params.pageSize;
      if (params?.department_id) queryParams.department_id = params.department_id;
      if (params?.strategic_goals) queryParams.strategic_goals = params.strategic_goals;
      if (params?.search) queryParams.search = params.search;
      if (params?.year) queryParams.year = params.year;

      return await processesApi.list(queryParams);
    },
    staleTime: STALE_TIMES.GOALS,
  });
}

export function useProcessDetailQuery(processId: number) {
  return useQuery({
    queryKey: queryKeys.processes.detail(processId),
    queryFn: () => processesApi.get(processId),
    enabled: !!processId,
    staleTime: STALE_TIMES.GOALS,
  });
}

export function useDeleteProcessMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (processId: number) => processesApi.delete(processId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.processes.all });
    },
  });
}

export function useCreateProcessMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => processesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.processes.all });
    },
  });
}

export function useUpdateProcessMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      processesApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.processes.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.processes.detail(variables.id) });
    },
  });
}

export default useProcessesQuery;
